import { useMemo } from "react";
import type { ReactNode } from "react";
import { Edit3, Trash2 } from "lucide-react";
import {
  CertificationDataTable,
  CertificationIconButton,
  type CertificationTableColumn,
} from "@modules/certificaciones/shared/ui";

export type Iso900114001TableColumn<TRow> = {
  key: string;
  header: string;
  render: (row: TRow) => ReactNode;
  className?: string;
};

type Props<TRow> = {
  title: string;
  rows: TRow[];
  columns: Iso900114001TableColumn<TRow>[];
  getRowKey: (row: TRow) => string | number;
  loading: boolean;
  error: string | null;
  createLabel: string;
  emptyTitle: string;
  onCreate: () => void;
  onEdit?: (row: TRow) => void;
  onDelete?: (row: TRow) => void;
  onRetry: () => void;
  pageSize?: number;
};

export function Iso900114001CrudTable<TRow>({
  title,
  rows,
  columns,
  getRowKey,
  loading,
  error,
  createLabel,
  emptyTitle,
  onCreate,
  onEdit,
  onDelete,
  onRetry,
  pageSize = 30,
}: Props<TRow>) {
  const adaptedColumns = useMemo<CertificationTableColumn<TRow>[]>(() => {
    const dataColumns = columns.map((column) => ({ ...column }));

    if (!onEdit && !onDelete) return dataColumns;

    return [
      {
        key: "acciones",
        header: "Acciones",
        className: "iso27001-actions-column",
        scrollable: false,
        render: (row) => (
          <div className="iso27001-row-actions">
            {onEdit ? (
              <CertificationIconButton
                label="Editar"
                icon={<Edit3 size={15} />}
                onClick={() => onEdit(row)}
              />
            ) : null}
            {onDelete ? (
              <CertificationIconButton
                label="Eliminar"
                icon={<Trash2 size={15} />}
                danger
                onClick={() => onDelete(row)}
              />
            ) : null}
          </div>
        ),
      },
      ...dataColumns,
    ];
  }, [columns, onDelete, onEdit]);

  return (
    <CertificationDataTable
      title={title}
      eyebrow="Registros"
      countLabel={`${rows.length} registros`}
      rows={rows}
      columns={adaptedColumns}
      getRowKey={getRowKey}
      loading={loading}
      error={error}
      emptyTitle={emptyTitle}
      emptyDescription="No hay registros para los criterios seleccionados."
      createLabel={createLabel}
      onCreate={onCreate}
      onRetry={onRetry}
      pageSize={pageSize}
      tableClassName="calidad-table iso9001-crud-table"
    />
  );
}
